import { Params } from "react-router-dom";
import { tldsList } from "../constants/constants";
import { capitalize } from "../utils/string";

export const addKeyInArray = (data: any) => {
  // TODO: _id Remove later
  return data?.map(
    (el: object & { id?: string; _id?: string }, idx: number) => ({
      ...el,
      key: el?.id || el?._id || `${idx}`,
    })
  );
};

export const selectSearchOption = (
  input: string,
  option?: { label: string; value: string | number }
) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

export const selectArrayOption = (
  data: any[],
  label: string,
  value: string | number,
  isCapitalLabel?: boolean
) => {
  return data?.map((el) => ({
    label: isCapitalLabel ? capitalize(el[label]) : el[label],
    value: typeof value === "number" ? Number(el[value]) : el[value] || null,
  }));
};

export const selectStringOption = (
  data: string[],
  option?: "number" | "string" | "boolean",
  isCapitalLabel?: boolean
) => {
  return data.map((el) => ({
    label: isCapitalLabel ? capitalize(el) : el,
    value: option === "number" ? Number(el) : el,
  }));
};

// generate back link for back button
export const backLink = (str: string, backCount: number = 1) => {
  const pathArray = str.split("/");
  return pathArray.slice(0, pathArray.length - backCount).join("/");
};

// url validation for antd form input
export const urlValidation = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  const urlRegex =
    /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;

  if (!urlRegex.test(value)) {
    return Promise.reject(new Error("Please enter a valid URL"));
  }
};

export const removeHttps = (url?: string) => {
  return url?.startsWith("https://") ? url.slice(8) : url;
};

// skip for get limited data (Pagination)
export const skipData = (page: number, limit: number) => {
  return limit * (page - 1);
};

// convert underscore to real string with capitalize
export const underscoreToCapitalizeString = (
  str: string,
  smallLetter?: boolean
) => {
  if (smallLetter) return str?.split("_").join(" ");
  return capitalize(str?.split("_").join(" "));
};

export const formatExtensions = (extensions: string[]): string => {
  return extensions.map((ext) => `.${ext}`).join(", ");
};

/**
 * Removes the parameter value from the end of the pathname.
 *
 * @param pathname - The full pathname string (e.g., "/path/to/resource/param").
 * @param params - An object containing route parameters (e.g., { key: "param" }).
 * @returns The sanitized pathname without the parameter value at the end.
 */
export const getSanitizedPathname = (
  pathname: string,
  params: Readonly<Params<string>>
): string => {
  const paramValues = Object.values(params);

  const pathParts = pathname.split("/");
  const lastSegment = pathParts[pathParts.length - 1];

  if (paramValues.includes(lastSegment)) {
    return pathParts.slice(0, -2).join("/");
  }

  return pathname;
};

export const convertToSnakeCase = (text: string) => {
  return text?.split(" ")?.join("_")?.toLowerCase();
};

export function hexToRgba(hex: string, alpha = 0.198) {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function extractTLD(email: string) {
  const match = email.match(/\.([a-zA-Z]{2,10})$/);
  return match ? match[1].toUpperCase() : null;
}

export function isValidTLD(email: string) {
  const tld = extractTLD(email);
  return tld ? tldsList.includes(tld) : false;
}

export const emailToUsername = (email: string): string => {
  const [username] = email.split("@");
  return username;
};

export function generateBatchCode(length: number): string {
  const numberPart = length.toString().padStart(6, "0");
  const batchCode = `B-${numberPart}`;

  return batchCode;
}

export function customTimeConverter(time12h: any) {
  const [time, modifier] = time12h.split(" ");
  let [hours, minutes, seconds] = time.split(":");

  hours = parseInt(hours, 10);

  // Special case: "12:00:00 PM" → "23:59:59"
  if (time === "12:00:00" && modifier === "PM") {
    return "23:59:59";
  }

  if (modifier === "AM") {
    if (hours === 12) hours = 0; // 12 AM → 00
  } else if (modifier === "PM") {
    if (hours !== 12) hours += 12; // normal PM conversion
  }

  // Format with leading zeros
  return [String(hours).padStart(2, "0"), minutes, seconds].join(":");
}

export function formatSouthAsianNumber(number: number = 0) {
  const numStr = number.toString();
  const lastThree = numStr.slice(-3);
  const otherNumbers = numStr.slice(0, -3);

  if (otherNumbers === "") return lastThree;

  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
}

export const formatNumber = (val: number | string): string => {
  const amount = Number(val) || 0;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export function formatPhoneNumber(number: string) {
  if (number?.startsWith("+88")) {
    return number;
  } else if (number?.startsWith("88")) {
    return "+" + number;
  } else {
    return "+88" + number;
  }
}

export function removeCountryCodeFromNumber(number: string) {
  if (!number) return "";

  // remove all spaces or hyphens if any
  let cleaned = number.replace(/[\s-]/g, "");

  // remove +88 or 88 from the beginning
  cleaned = cleaned.replace(/^(\+?88)/, "");

  // return only the final local number (must start with 01)
  return cleaned;
}

export function formatSPayNumber(number: string) {
  if (number?.startsWith("88")) {
    return number;
  } else {
    return "88" + number;
  }
}

// utils/cn.ts
type ClassValue =
  | string
  | number
  | null
  | undefined
  | ClassDictionary
  | ClassArray;
interface ClassDictionary {
  [key: string]: boolean | null | undefined;
}
interface ClassArray extends Array<ClassValue> {}

export function cn(...inputs: ClassValue[]): string {
  const classes: string[] = [];

  const process = (value: ClassValue): void => {
    if (!value) return;

    if (typeof value === "string" || typeof value === "number") {
      classes.push(String(value));
    } else if (Array.isArray(value)) {
      value.forEach(process);
    } else if (typeof value === "object") {
      for (const [key, condition] of Object.entries(value)) {
        if (condition) classes.push(key);
      }
    }
  };

  inputs.forEach(process);
  return classes.join(" ");
}
